import { useCallback } from "react";
import * as Sentry from "@sentry/react";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function useApi() {
  const authFetch = useCallback(async (path: string, options: RequestInit = {}) => {
    const baseUrl = import.meta.env.VITE_API_URL || "";

    let response: Response;

    try {
      response = await fetch(`${baseUrl}${path}`, {
        ...options,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("api.path", path);
        scope.setTag("api.failure_kind", "network");
        scope.setContext("api_request", {
          method: options.method || "GET",
          path,
        });
        Sentry.captureException(error);
      });
      throw error;
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new ApiError(
        body.error || `API error: ${response.status}`,
        response.status,
        body.code
      );

      if (response.status >= 500) {
        Sentry.withScope((scope) => {
          scope.setTag("api.path", path);
          scope.setTag("api.status_code", String(response.status));
          if (body.code) {
            scope.setTag("api.code", String(body.code));
          }
          scope.setContext("api_response", {
            method: options.method || "GET",
            path,
            status: response.status,
          });
          Sentry.captureException(error);
        });
      }

      throw error;
    }

    return response.json();
  }, []);

  return { authFetch };
}
